import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

/**
 * useAccountActions - Unified account actions for menus and Quick Actions
 *
 * Provides:
 * - viewProfile: Navigate to profile settings
 * - handleSignOut: Robust logout with cache clearing and redirect
 */
export function useAccountActions() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const viewProfile = () => {
    navigate('/settings/profile');
  };

  const handleSignOut = async () => {
    try {
      // 1. Sign out from Firebase
      await signOut(auth);

      // 2. Clear React Query cache (all queries and mutations)
      queryClient.clear();

      // 3. Clear localStorage (except safe keys like theme/language)
      const keysToKeep = ['theme', 'language', 'i18nextLng'];
      Object.keys(localStorage).forEach((key) => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });

      // 4. Clear sessionStorage completely
      sessionStorage.clear();

      // 5. Note: Firestore offline persistence is cleared automatically on signOut
      // No need to manually call clearIndexedDbPersistence

      // 6. Success toast
      toast.success(t('toasts.account.loggedOut'), {
        description: t('toasts.account.loggedOutDescription'),
      });

      // 7. Redirect to login (use window.location for full page reload to clear all state)
      window.location.assign('/login');
    } catch (error) {
      logger.error('[useAccountActions] Sign out error:', error);
      toast.error(t('toasts.account.logoutFailed'), {
        description: t('toasts.account.logoutFailedDescription'),
      });
    }
  };

  return {
    viewProfile,
    handleSignOut,
  };
}
